import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { useNavigate, useParams } from "react-router-dom"
import {
  Card,
  CardBody,
  Button,
  Input,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import {
  FaFileAlt,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaFileExcel,
  FaEdit,
  FaEye,
  FaCheck,
  FaTimes,
  FaDownload,
  FaArrowLeft,
  FaUpload,
} from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import Breadcrumb from "components/Common/Breadcrumb"
import Loader from "components/Loader/loader"
import Swal from "sweetalert2"
import {
  fetchData,
  FetchDeleteData,
  FetchPutData,
} from "utils/Common_api_functions"
import Configs from "../../config"
import moment from "moment"
import secureLocalStorage from "react-secure-storage"

const getCurrentUserId = () => {
  try {
    const user = secureLocalStorage?.getItem("admin_user")
    return String(user?.id || user?.userId || "")
  } catch {
    return ""
  }
}

const getFileIcon = (fileName, size = 18) => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  if (ext === "pdf") return <FaFilePdf size={size} color="#e74c3c" />
  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext))
    return <FaFileImage size={size} color="#27ae60" />
  if (["doc", "docx"].includes(ext))
    return <FaFileWord size={size} color="#2980b9" />
  if (["xls", "xlsx", "csv"].includes(ext))
    return <FaFileExcel size={size} color="#16a085" />
  return <FaFileAlt size={size} color="#7f8c8d" />
}

const isImage = fileName =>
  ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(
    fileName?.split(".").pop()?.toLowerCase()
  )
const isPdf = fileName => fileName?.split(".").pop()?.toLowerCase() === "pdf"

const fixPath = path => {
  if (!path) return ""
  const sharedIndex = path.search(/shared[/\\]/i)
  if (sharedIndex !== -1) path = path.slice(sharedIndex + 7)
  return path.replace(/\\/g, "/")
}

// ── PDF Viewer ───────────────────────────────────────────────────────
const PdfViewer = ({ url }) => {
  const containerRef = useRef(null)
  const [numPages, setNumPages] = useState(0)
  const [loading, setPdfLoading] = useState(true)
  const [error, setError] = useState(null)
  const renderedRef = useRef(false)

  const renderPdf = useCallback(async () => {
    if (renderedRef.current) return
    renderedRef.current = true
    try {
      if (!window.pdfjsLib) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script")
          script.src =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js"
          script.onload = resolve
          script.onerror = reject
          document.head.appendChild(script)
        })
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js"
      }
      const pdf = await window.pdfjsLib.getDocument(url).promise
      setNumPages(pdf.numPages)
      if (!containerRef.current) return
      containerRef.current.innerHTML = ""
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum)
        const viewport = page.getViewport({ scale: 1.5 })
        const canvas = document.createElement("canvas")
        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.cssText =
          "width:100%;display:block;margin-bottom:12px;border-radius:6px;box-shadow:0 2px 8px rgba(0,0,0,0.15)"
        containerRef.current.appendChild(canvas)
        await page.render({ canvasContext: canvas.getContext("2d"), viewport })
          .promise
      }
      setPdfLoading(false)
    } catch {
      setError("Failed to load PDF.")
      setPdfLoading(false)
    }
  }, [url])

  useEffect(() => {
    renderedRef.current = false
    setPdfLoading(true)
    setError(null)
    setNumPages(0)
    renderPdf()
  }, [url])

  if (error) return <div className="text-danger p-3">{error}</div>
  return (
    <div>
      {loading && (
        <div className="text-center py-4 text-muted">Loading PDF...</div>
      )}
      {!loading && numPages > 0 && (
        <div className="text-muted small mb-2">
          {numPages} page{numPages > 1 ? "s" : ""}
        </div>
      )}
      <div ref={containerRef} />
    </div>
  )
}

const getSystemAppHint = fileName => {
  const ext = fileName?.split(".").pop()?.toLowerCase()
  const ua = navigator.userAgent
  const isWindows = ua.includes("Windows")
  const isMac = ua.includes("Mac")
  const map = {
    docx: isWindows
      ? "Microsoft Word"
      : isMac
      ? "Pages / Word"
      : "LibreOffice Writer",
    doc: isWindows
      ? "Microsoft Word"
      : isMac
      ? "Pages / Word"
      : "LibreOffice Writer",
    xlsx: isWindows
      ? "Microsoft Excel"
      : isMac
      ? "Numbers / Excel"
      : "LibreOffice Calc",
    xls: isWindows
      ? "Microsoft Excel"
      : isMac
      ? "Numbers / Excel"
      : "LibreOffice Calc",
    pptx: isWindows
      ? "Microsoft PowerPoint"
      : isMac
      ? "Keynote / PowerPoint"
      : "LibreOffice Impress",
    zip: "File Explorer / Finder",
    mp4: "Video Player",
    mp3: "Music Player",
  }
  return map[ext] || "Default System App"
}

const openInSystemApp = url => {
  const ext = url?.split(".").pop()?.toLowerCase()
  if (["doc", "docx"].includes(ext))
    window.location.href = `ms-word:ofe|u|${url}`
  else if (["xls", "xlsx"].includes(ext))
    window.location.href = `ms-excel:ofe|u|${url}`
  else if (["ppt", "pptx"].includes(ext))
    window.location.href = `ms-powerpoint:ofe|u|${url}`
  else window.location.href = url
}

// ── Main Component ───────────────────────────────────────────────────
const DocumentView = () => {
  document.title = "Document View | Siddhi Tax Consultant"
  const navigate = useNavigate()
  const { id: projectId } = useParams()
  const currentUserId = getCurrentUserId()

  const [loading, setLoading] = useState(false)
  const [project, setProject] = useState(null)
  const [documents, setDocuments] = useState([])
  const [myPermission, setMyPermission] = useState({
    view: 0,
    edit: 0,
    delete: 0,
  })

  const [searchString, setSearchString] = useState("")
  const [pageSize, setPageSize] = useState(50)
  const [currentPage, setCurrentPage] = useState(1)

  // ── Replace File Modal state ──────────────────────────────────────
  const [replaceDoc, setReplaceDoc] = useState(null) // doc being replaced
  const [replaceFile, setReplaceFile] = useState(null) // new File object
  const [replaceUploading, setReplaceUploading] = useState(false)
  const replaceInputRef = useRef(null)

  // preview modal
  const [previewDoc, setPreviewDoc] = useState(null)

  useEffect(() => {
    getProjectData()
  }, [projectId])
  useEffect(() => {
    setCurrentPage(1)
  }, [searchString, pageSize])

  async function getProjectData() {
    const APIdata = await fetchData(setLoading, `projects/${projectId}`)
    if (APIdata?.success) {
      const data = APIdata.data
      const perms = JSON.parse(data.permissions || "[]")
      const mine = perms.find(p => String(p.userId) === String(currentUserId))
      setProject(data)
      setDocuments(
        Number(mine?.viewglobal) === 1
          ? data.files
          : data?.files?.filter(
              item => Number(item?.userId) === Number(currentUserId)
            )
      )
      try {
        if (mine)
          setMyPermission({
            view: Number(mine.view),
            edit: Number(mine.edit),
            delete: Number(mine.delete),
          })
      } catch {
        /* keep defaults */
      }
    }
  }

  const filteredData = useMemo(() => {
    const q = searchString.trim().toLowerCase()
    if (!q) return documents
    return documents.filter(
      doc =>
        doc?.fileName?.toLowerCase().includes(q) ||
        doc?.user?.name?.toLowerCase().includes(q) ||
        (doc?.fileUrl || "").split(".").pop()?.toLowerCase().includes(q)
    )
  }, [documents, searchString])

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize
  const paginatedData = useMemo(
    () => filteredData.slice(start, start + pageSize),
    [filteredData, currentPage, pageSize]
  )

  // ── Replace File handlers ─────────────────────────────────────────
  const openReplaceModal = doc => {
    setReplaceDoc(doc)
    setReplaceFile(null)
  }

  const closeReplaceModal = () => {
    setReplaceDoc(null)
    setReplaceFile(null)
    setReplaceUploading(false)
  }

  const handleReplaceFileChange = e => {
    const file = e.target.files?.[0]
    if (file) setReplaceFile(file)
  }

  const handleReplaceSubmit = async () => {
    if (!replaceFile || !replaceDoc) return

    setReplaceUploading(true)

    const APIdata = await FetchPutData(
      setLoading,
      "project/file",
      replaceDoc?.id,
      {
        file: replaceFile,
        fileName: replaceFile?.name,
      }
    )

    if (APIdata?.data?.success) {
      Swal.fire({
        title: "File Replaced!",
        icon: "success",
        timer: 1500,
      })
      getProjectData()
      closeReplaceModal()
    } else {
      Swal.fire({
        title: "Error replacing file.",
        text: result?.message,
        icon: "error",
      })
      setReplaceUploading(false)
    }
  }

  // ── Delete ────────────────────────────────────────────────────────
  const handleDelete = doc => {
    Swal.fire({
      title: `Delete "${doc?.fileName}"?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(
          setLoading,
          "project/file",
          doc.id
        )
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Deleted!",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          })
          setDocuments(prev => prev.filter(d => d.id !== doc.id))
        } else {
          Swal.fire({
            title: "Error deleting file.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  // ── Download ──────────────────────────────────────────────────────
  const handleDownload = async doc => {
    const url = `${Configs.imageUrl}/uploads/${fixPath(doc.fileUrl)}`
    const response = await fetch(url)
    const blob = await response.blob()
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = doc.fileName || "file"
    a.click()
    URL.revokeObjectURL(a.href)
  }

  const previewUrl = previewDoc
    ? `${Configs.imageUrl}/uploads/${fixPath(previewDoc.fileUrl)}`
    : ""
  const previewFileName = previewDoc?.fileUrl || previewDoc?.fileName || ""

  return (
    <div className="page-content">
      {loading && <Loader />}

      {/* ── Header ─────────────────────────────────────────────── */}
      <Breadcrumb title="Departments" breadcrumbItem="Document View" />
      <Card>
        <CardBody>
          <div className="d-flex align-items-center justify-content-between mb-3 w-100">
            <div className="d-flex gap-3 align-items-center">
              <h5 className="mb-0 fw-bold">
                {project?.projectName || "Department Documents"}
              </h5>
            </div>
            <div className="d-flex align-items-center justify-content-end gap-2 w-75">
              <Input
                type="select"
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="rounded-4 w-auto pe-5"
              >
                <option value={50}>50</option>
                <option value={150}>150</option>
                <option value={200}>200</option>
              </Input>
              <Input
                placeholder="Search files…"
                value={searchString}
                onChange={e => setSearchString(e.target.value)}
                style={{ maxWidth: 200 }}
                className="rounded-4"
              />
              <Button
                color="primary"
                className="rounded-4 w-auto"
                onClick={() =>
                  window.open(
                    `${Configs.appurl}projects/documents/${projectId}`,
                    "_blank"
                  )
                }
              >
                <FaUpload className="me-1" /> Upload
              </Button>
              <Button
                color="secondary"
                className="rounded-4"
                onClick={() => navigate(-1)}
              >
                <FaArrowLeft className="me-1" /> Back
              </Button>
            </div>
          </div>

          {/* ── Table ────────────────────────────────────────────── */}
          <Table responsive bordered hover className="align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Name</th>
                <th>Uploaded By</th>
                <th>Uploaded At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {searchString
                      ? "No documents match your search."
                      : "No documents uploaded yet."}
                  </td>
                </tr>
              ) : (
                paginatedData.map((doc, index) => (
                  <tr key={doc.id}>
                    <td>{start + index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center gap-1">
                        {getFileIcon(doc.fileUrl || doc.fileName)}
                        <small className="text-muted">
                          {(doc.fileUrl || doc.fileName)
                            ?.split(".")
                            .pop()
                            ?.toUpperCase() || "—"}
                        </small>
                      </div>
                    </td>
                    <td>{doc.fileName || "—"}</td>
                    <td>{doc?.user?.name || "—"}</td>
                    <td>
                      {doc.createdAt
                        ? moment(doc.createdAt).format("DD MMM YYYY, hh:mm A")
                        : "—"}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {(myPermission.view === 1 ||
                          myPermission.viewglobal === 1) && (
                          <Button
                            color="info"
                            className="rounded-3"
                            onClick={() => setPreviewDoc(doc)}
                            title="Preview"
                          >
                            <FaEye />
                          </Button>
                        )}
                        {myPermission.edit === 1 && (
                          <Button
                            color="warning"
                            className="rounded-3"
                            onClick={() => openReplaceModal(doc)}
                            title="Replace File"
                          >
                            <FaEdit />
                          </Button>
                        )}
                        {(myPermission.view === 1 ||
                          myPermission.viewglobal === 1) && (
                          <Button
                            color="success"
                            className="rounded-3"
                            onClick={() => handleDownload(doc)}
                            title="Download"
                          >
                            <FaDownload />
                          </Button>
                        )}
                        {myPermission.delete === 1 && (
                          <Button
                            color="danger"
                            className="rounded-3"
                            onClick={() => handleDelete(doc)}
                            title="Delete"
                          >
                            <MdDelete />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* ── Pagination ───────────────────────────────────────── */}
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-2">
            <span className="text-muted small">
              Showing {start + 1}–
              {Math.min(start + pageSize, filteredData.length)} of{" "}
              {filteredData.length} entries
            </span>
            <div className="d-flex gap-1 flex-wrap">
              <Button
                size="sm"
                outline
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              >
                Previous
              </Button>
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1
                return (
                  <Button
                    key={pageNum}
                    size="sm"
                    color={currentPage === pageNum ? "primary" : "light"}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                )
              })}
              <Button
                size="sm"
                outline
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              >
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ── Replace File Modal ────────────────────────────────────── */}
      <Modal isOpen={!!replaceDoc} toggle={closeReplaceModal} centered>
        <ModalHeader toggle={closeReplaceModal}>
          <FaUpload className="me-2 text-white" />
          Replace File
        </ModalHeader>
        <ModalBody>
          {replaceDoc && (
            <div className="mb-3">
              <div className="text-muted small mb-1">Current file:</div>
              <div className="d-flex align-items-center gap-2 p-2 bg-light rounded-3">
                {getFileIcon(replaceDoc.fileUrl || replaceDoc.fileName, 20)}
                <span className="fw-medium">{replaceDoc.fileName}</span>
              </div>
            </div>
          )}
          <div>
            <div className="text-muted small mb-2">
              Select new file to replace it with:
            </div>

            {/* Hidden file input */}
            <input
              ref={replaceInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleReplaceFileChange}
            />

            {/* Custom upload area */}
            <div
              onClick={() => replaceInputRef.current?.click()}
              style={{
                border: "2px dashed #ccc",
                borderRadius: 10,
                padding: "28px 20px",
                textAlign: "center",
                cursor: "pointer",
                background: replaceFile ? "#f0fff4" : "#fafafa",
                borderColor: replaceFile ? "#27ae60" : "#ccc",
                transition: "all 0.2s",
              }}
            >
              {replaceFile ? (
                <div className="d-flex flex-column align-items-center gap-2">
                  {getFileIcon(replaceFile.name, 36)}
                  <span className="fw-medium text-success">
                    {replaceFile.name}
                  </span>
                  <span className="text-muted small">
                    ({(replaceFile.size / 1024).toFixed(1)} KB) — click to
                    change
                  </span>
                </div>
              ) : (
                <div className="d-flex flex-column align-items-center gap-2 text-muted">
                  <FaUpload size={32} />
                  <span>Click to browse file</span>
                </div>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            disabled={!replaceFile || replaceUploading}
            onClick={handleReplaceSubmit}
          >
            {replaceUploading ? (
              "Uploading…"
            ) : (
              <>
                <FaCheck className="me-1" /> Replace File
              </>
            )}
          </Button>
          <Button color="secondary" onClick={closeReplaceModal}>
            <FaTimes className="me-1" /> Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* ── Preview Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={!!previewDoc}
        toggle={() => setPreviewDoc(null)}
        centered
        size="xl"
      >
        <ModalHeader toggle={() => setPreviewDoc(null)}>
          {previewDoc && getFileIcon(previewFileName, 16)}
          <span className="ms-2">{previewDoc?.fileName || "File Preview"}</span>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {previewDoc &&
            (isPdf(previewFileName) ? (
              <PdfViewer url={previewUrl} />
            ) : isImage(previewFileName) ? (
              <div className="text-center">
                <img
                  src={previewUrl}
                  alt={previewDoc.fileName}
                  style={{ maxWidth: "100%", borderRadius: 8 }}
                />
              </div>
            ) : (
              <div className="text-center py-5">
                {getFileIcon(previewFileName, 60)}
                <p className="mt-3 text-muted">
                  Preview is not available for this file type.
                </p>
                <p className="text-muted small">
                  Will open in:{" "}
                  <strong>{getSystemAppHint(previewFileName)}</strong>
                </p>
                <Button
                  color="primary"
                  onClick={() => openInSystemApp(previewUrl)}
                >
                  Open in {getSystemAppHint(previewFileName)}
                </Button>
              </div>
            ))}
        </ModalBody>
        <ModalFooter>
          {myPermission.edit === 1 && previewDoc && (
            <Button color="success" onClick={() => handleDownload(previewDoc)}>
              <FaDownload className="me-1" /> Download
            </Button>
          )}
          <Button color="secondary" onClick={() => setPreviewDoc(null)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default DocumentView
